import { api } from '@/lib/api-client';
import { listTagsApi, type Paginated } from '@/features/skills/api';
import type { SubagentDetail } from './types';

export { listTagsApi };
export type { Paginated };

export interface ListSubagentsParams {
  search?: string;
  tags?: string[];
  page?: number;
  pageSize?: number;
}

export async function listSubagentsApi(
  params: ListSubagentsParams,
): Promise<Paginated<SubagentDetail>> {
  const search = params.search?.trim();
  const { data } = await api.get<Paginated<SubagentDetail>>('/subagents', {
    params: {
      ...(search ? { search } : {}),
      ...(params.tags?.length ? { tags: params.tags.join(',') } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    },
  });
  return data;
}

export async function getSubagentApi(id: string): Promise<SubagentDetail> {
  const { data } = await api.get<{ subagent: SubagentDetail }>(`/subagents/${id}`);
  return data.subagent;
}

export interface CreateSubagentInput {
  file: File;
  name?: string;
  description?: string;
  tags: string[];
}

export async function createSubagentApi(input: CreateSubagentInput): Promise<SubagentDetail> {
  const form = new FormData();
  form.append('file', input.file);
  if (input.name !== undefined) form.append('name', input.name);
  if (input.description !== undefined) form.append('description', input.description);
  form.append('tags', JSON.stringify(input.tags));
  const { data } = await api.post<{ subagent: SubagentDetail }>('/subagents', form);
  return data.subagent;
}

export interface UpdateSubagentInput {
  id: string;
  file?: File | null;
  name?: string;
  description?: string;
  tags?: string[];
}

export async function updateSubagentApi(input: UpdateSubagentInput): Promise<SubagentDetail> {
  const form = new FormData();
  if (input.file) form.append('file', input.file);
  if (input.name !== undefined) form.append('name', input.name);
  if (input.description !== undefined) form.append('description', input.description);
  if (input.tags !== undefined) form.append('tags', JSON.stringify(input.tags));
  const { data } = await api.put<{ subagent: SubagentDetail }>(`/subagents/${input.id}`, form);
  return data.subagent;
}

export async function deleteSubagentApi(id: string): Promise<void> {
  await api.delete(`/subagents/${id}`);
}

export function getSubagentDownloadUrl(id: string): string {
  const base = (api.defaults.baseURL ?? '/api/v1').replace(/\/$/, '');
  return `${base}/subagents/${id}/download`;
}

export async function downloadSubagentApi(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await api.get<Blob>(`/subagents/${id}/download`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'] ?? '';
  // RFC 5987 / 6266: filename="..." (sem aspas se sem espaços, mas o backend sempre manda com)
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? `subagente-${id}.md`;
  return { blob: res.data, filename };
}
